/**
 * One-shot signal that the US FEB consent copy was actually rendered on the login screen for the
 * current login attempt (CASHDAP-3450).
 *
 * The copy on /login is gated by a `useFlag` read against the PRE-identify LaunchDarkly context,
 * while the post-login consent POST re-reads the flag against the identified consumer context
 * (`useReadyFeatureFlag`). Under a per-user rollout those two evaluations can disagree, and the
 * POST must never record a consent whose copy was never displayed — so the display itself is
 * remembered here and required by the POST, instead of being re-derived from a different LD
 * context.
 *
 * Same design rationale as flow/remember-me/loginSignal.ts: plain module state, never persisted,
 * set during client render effects and consumed in a client effect, so a reload simply drops the
 * mark and the consent capture defers to the next login. Cleared on consumption so a later login
 * in the same tab cannot reuse a stale display from a previous attempt.
 */
let usFebConsentCopyDisplayed = false

export const markUsFebConsentCopyDisplayed = (): void => {
  usFebConsentCopyDisplayed = true
}

/**
 * The login page calls this when it mounts WITHOUT the consent copy, so the mark always reflects
 * the latest login attempt in this tab. Without it, a mark set by an earlier abandoned attempt
 * (or before a mid-session flag flip) would survive and vouch for a later login that never
 * rendered the copy — a false consent record.
 */
export const clearUsFebConsentCopyDisplayed = (): void => {
  usFebConsentCopyDisplayed = false
}

export const consumeUsFebConsentCopyDisplayed = (): boolean => {
  const displayed = usFebConsentCopyDisplayed
  usFebConsentCopyDisplayed = false
  return displayed
}
